// src/components/cloud/FileList.jsx
import React from 'react';
import {
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Paper, IconButton, Typography, Box, Tooltip, Link as MuiLink, Checkbox
} from '@mui/material';
import { FaFolder, FaFile, FaDownload, FaTrash, FaEdit } from 'react-icons/fa';
import { useDrag, useDrop } from 'react-dnd';


const FileList = ({
    files = [],
    selectedFiles,
    onFileSelect,
    onSelectAll,
    onNavigate,
    onDownload,
    onDelete,
    onRename,
    isSearchView = false,
}) => {
    

    const formatBytes = (bytes, decimals = 2) => {
        if (!+bytes) return '0 Bytes';
        const k = 1024;
        const dm = decimals < 0 ? 0 : decimals;
        const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return `${parseFloat((bytes / Math.pow(k, i)).toFixed(dm))} ${sizes[i]}`;
    };

    const getDisplayName = (file) => {
        // Se siamo in modalità ricerca, mostra sempre il percorso completo
        if (isSearchView) {
            return file.name;
        }
        // Altrimenti, usa la logica di prima
        if (file.isDir) {
            if (file.isParent) return '.. (Cartella superiore)';
            return file.name.split('/').slice(-2, -1)[0];
        }
        return file.name.split('/').pop();
    };
    
    // Funzione helper per visualizzare il percorso sotto il nome del file nella ricerca
    const getDisplayPath = (filePath) => {
        const parts = filePath.split('/');
        parts.pop(); // Rimuovi il nome del file
        if (parts.length === 0) return "Nella cartella principale";
        return `In: /${parts.join('/')}`;
    };

    const numSelected = selectedFiles.length;
    const rowCount = files.filter(f => !f.isDir && !f.isParent).length; // Solo i file possono essere selezionati

    return (
    <TableContainer component={Paper} variant="outlined" sx={{ mt: 2, overflow: 'auto' }}>
        <Table sx={{ minWidth: 650 }} aria-label="file list">
            <TableHead sx={{ bgcolor: 'action.hover' }}>
                <TableRow>
                    <TableCell padding="checkbox">
                        <Tooltip title="Seleziona tutti i file">
                            <Checkbox
                                color="primary"
                                indeterminate={numSelected > 0 && numSelected < rowCount}
                                checked={rowCount > 0 && numSelected === rowCount}
                                onChange={onSelectAll}
                                inputProps={{ 'aria-label': 'seleziona tutti i file' }}
                            />
                        </Tooltip>
                    </TableCell>
                    <TableCell>Nome</TableCell>
                    <TableCell align="right">Dimensione</TableCell>
                    <TableCell>Ultima Modifica</TableCell>
                    <TableCell align="center">Azioni</TableCell>
                </TableRow>
            </TableHead>
            <TableBody>
                {files.length === 0 && (
                    <TableRow>
                        <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                            <Typography color="text.secondary">
                                Questa cartella è vuota.
                            </Typography>
                        </TableCell>
                    </TableRow>
                )}
                {files.map((file) => {
                    const isSelected = selectedFiles.includes(file.name);
                    const isSelectable = !file.isDir && !file.isParent;
                    
                    return (
                        <TableRow
                            key={file.name}
                            hover
                            role="checkbox"
                            aria-checked={isSelected}
                            selected={isSelected}
                            onClick={() => isSelectable && onFileSelect(file.name)}
                            sx={{ cursor: isSelectable ? 'pointer' : 'default' }}
                        >
                            <TableCell padding="checkbox">
                                <Checkbox
                                    color="primary"
                                    checked={isSelected}
                                    onChange={() => isSelectable && onFileSelect(file.name)}
                                    inputProps={{ 'aria-labelledby': file.name }}
                                />
                            </TableCell>

                            <TableCell component="th" scope="row" sx={{ py: 1 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                    {file.isDir ? (
                                        <FaFolder style={{ color: '#ffc107', fontSize: '1.4em', minWidth: '20px' }} />
                                    ) : (
                                        <FaFile style={{ color: '#6c757d', fontSize: '1.4em', minWidth: '20px' }} />
                                    )}
                                    {file.isDir ? (
                                        <MuiLink
                                            component="button"
                                            variant="body1"
                                            onClick={() => onNavigate(file.name)}
                                            sx={{ 
                                                fontWeight: 500, 
                                                textAlign: 'left',
                                                whiteSpace: 'nowrap',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                            }}
                                        >
                                            {getDisplayName(file)}
                                        </MuiLink>
                                    ) : (
                                        <Typography 
                                            variant="body1"
                                            sx={{
                                                whiteSpace: 'nowrap',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                            }}
                                        >
                                            {getDisplayName(file)}
                                        </Typography>
                                    )}
                                    {isSearchView && (
                                                <Typography variant="caption" color="text.secondary" component="div">
                                                    {getDisplayPath(file.name)}
                                                </Typography>
                                            )}
                                </Box>
                            </TableCell>

                            <TableCell align="right">
                                {file.isDir ? '—' : formatBytes(file.size)}
                            </TableCell>

                            <TableCell>
                                {file.isDir ? '—' : new Date(file.lastModified).toLocaleString()}
                            </TableCell>

                            <TableCell align="center" sx={{ py: 1 }}>
                                {/* Mostra le azioni se non è la cartella "indietro" */}
                                {!file.isParent && (
                                    <Box>
                                        <Tooltip title="Rinomina">
                                            <IconButton onClick={(e) => { e.stopPropagation(); onRename(file.name, file.isDir); }} size="small">
                                                <FaEdit />
                                            </IconButton>
                                        </Tooltip>
                                        
                                        {/* Il download è solo per i file */}
                                        {!file.isDir && (
                                            <Tooltip title="Download">
                                                <IconButton onClick={(e) => { e.stopPropagation(); onDownload(file.name); }} size="small">
                                                    <FaDownload />
                                                </IconButton>
                                            </Tooltip>
                                        )}
                                        {!file.isDir && (
                                        <Tooltip title="Elimina">
                                            <IconButton onClick={(e) => { e.stopPropagation(); onDelete(file.name, file.isDir); }} size="small" sx={{ color: 'error.main' }}>
                                                <FaTrash />
                                            </IconButton>
                                        </Tooltip>
                                        )}
                                    </Box>
                                )}
                            </TableCell>
                        </TableRow>
                    );
                })}
            </TableBody>
        </Table>
    </TableContainer>
);
};

export default FileList;